
import { MainLayout } from "@/components/layout/MainLayout";
import { useState, useEffect } from "react";
import { FileItem } from "../admin/files/types";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// Type for QA items from Supabase
type QAItem = {
  id: string;
  question: string;
  answer: string;
  category: string;
  is_active: boolean;
  created_at: string;
}

export default function QAQuestions() {
  const [qaItems, setQaItems] = useState<FileItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  
  // Load Q&A items from Supabase
  useEffect(() => {
    async function fetchQAItems() {
      setIsLoading(true);
      try {
        // Only fetch active items for member view
        const response = await (supabase as any)
          .from('qa_items')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false });
          
        if (response.error) {
          console.error("Error fetching Q&A items:", response.error);
          return;
        }
        
        // Convert Supabase data to FileItem format
        const formattedItems: FileItem[] = response.data.map((item: QAItem) => ({
          id: parseInt(item.id.substring(0, 8), 16), // Generate numeric ID from UUID
          name: item.question.substring(0, 50),
          type: "qa",
          size: "1 KB",
          uploadDate: new Date(item.created_at).toISOString().split('T')[0],
          url: "",
          category: item.category,
          question: item.question,
          answer: item.answer,
          isActive: item.is_active
        }));
        
        setQaItems(formattedItems);
      } catch (error) {
        console.error("Error in fetchQAItems:", error);
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchQAItems();
  }, []);
  
  // Filter Q&A items by category and search query
  const filteredQAItems = qaItems.filter(item => {
    const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      (item.question && item.question.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.answer && item.answer.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });
  
  // Get unique categories from Q&A items
  const categories = [...new Set(qaItems.map(item => item.category))];
  
  return (
    <MainLayout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Ερωτήσεις & Απαντήσεις</h1>
        <p className="text-muted-foreground mb-6">
          Συχνές ερωτήσεις και απαντήσεις για τεχνικά θέματα.
        </p>
        
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Αναζήτηση ερωτήσεων..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full md:w-[200px]">
              <SelectValue placeholder="Επιλογή κατηγορίας" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Όλες οι κατηγορίες</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category || 'unknown'} value={category || 'unknown'}>
                  {getCategoryLabel(category || 'unknown')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : filteredQAItems.length === 0 ? (
          <div className="text-center py-8 border rounded-lg">
            <p className="text-muted-foreground">Δεν βρέθηκαν ερωτήσεις & απαντήσεις.</p>
          </div>
        ) : (
          <Accordion type="multiple" className="w-full space-y-4">
            {filteredQAItems.map((item) => (
              <AccordionItem 
                key={item.id} 
                value={`item-${item.id}`}
                className="border rounded-lg px-4"
              >
                <AccordionTrigger className="text-left py-4 hover:no-underline font-medium">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-1 text-muted-foreground whitespace-pre-line">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </MainLayout>
  );
}

function getCategoryLabel(value: string): string {
  const categories: Record<string, string> = {
    "documents": "Πρότυπες μελέτες",
    "videos": "Εκπαιδευτικά Video",
    "guides": "Goto Οδηγίες",
    "webinars": "Webinars",
    "qa": "Q&A"
  };
  
  return categories[value] || value;
}
